import React from 'react';

/**
 * Provides ability to restore vertical scroll position of particular Component viewport on browser back button click
 * Depends on `withRouter` and `withLogger` HoCs, but the latter is an optional
 *
 * @returns {function(*): function(*=, *): *}
 */
export const withRestoreScrollPosition = (timeout, skipResetState = false) => (Component) => {
  const RestoreScrollPosition = (props) => {
    const { history, logger = { debug: () => null } } = props;

    const scrollAreaRef = React.useRef(null);
    const [hasBeenScrollRestored, setHasBeenScrollRestored] = React.useState(false);

    // callback which should be invoked on before navigation to specific page with cache data (e.g. snippet of store state)
    // This data is gonna be used to restore page state with vertical scroll position on return (history pop state)
    const saveScrollPosition = (cachedDataForFutureRestore, node) => {
      const scrollTop = node?.scrollTop || scrollAreaRef.current?.scrollTop;
      const currentState = history.location.state || {};
      history.replace({
        search: history.location.search,
        state: {
          ...currentState,
          restoreScrollPosition: {
            scrollTop,
            cache: cachedDataForFutureRestore,
          },
        },
      });

      logger.debug('Scroll position has been saved', {
        initiator: 'withRestoreScrollPosition',
        scrollTop,
        cache: cachedDataForFutureRestore,
      });
    };

    // Restore scroll position in case previous state is available
    React.useLayoutEffect(() => {
      const { scrollTop: prevScrollTop, cache } = history.location.state?.restoreScrollPosition || {};

      if (prevScrollTop && scrollAreaRef.current) {
        // restore scroll position
        if (timeout) {
          setTimeout(() => scrollAreaRef.current.scrollTo(0, prevScrollTop), timeout);
        } else {
          scrollAreaRef.current.scrollTo(0, prevScrollTop);
        }

        logger.debug('Page scroll position has been restore from previous visit', {
          initiator: 'useRestoreScrollPosition',
          scrollTop: prevScrollTop,
          cache,
          location: window.location.href,
        });

        setHasBeenScrollRestored(true);
      }
    }, [scrollAreaRef.current]);

    const resetScrollCache = () => {
      const currentState = history.location.state || {};
      history.replace({
        search: history.location.search,
        state: {
          ...currentState,
          restoreScrollPosition: null,
        },
      });
    };

    const newProps = {
      restoreScrollCache: history.location.state?.restoreScrollPosition?.cache || {},
      scrollAreaRef,
      saveScrollPosition,
      resetScrollCache,
    };

    // reset cached data in history state as soon as scroll has been restored
    React.useEffect(() => {
      if (hasBeenScrollRestored && history.location.state?.restoreScrollPosition && !skipResetState) {
        resetScrollCache();
      }
    }, [hasBeenScrollRestored]);

    return <Component {...props} {...newProps} />;
  };

  RestoreScrollPosition.displayName = `withRestoreScrollPosition(${Component.displayName || Component.name})`;

  return RestoreScrollPosition;
};

export default withRestoreScrollPosition;
